import { createMemo, createSignal, For } from 'solid-js';
import { Placements } from '@24hr/gullvikspraktikan-entities';
import { Select } from '../select/select';
import { Contact, ContactArea, ContactsProps } from './contacts.types';
import {
    StyledContacts,
    StyledContactHeadline,
    StyledContactsList,
    StyledSelectWrapper,
    StyledNoContactsContainer,
    StyledAddressContainer,
    StyledAreaHeader,
    StyledGenericContactWrapper,
} from './contacts.styles';
import { colors } from '../../style/style-variables';
import { SelectOption } from '../select/select.types';
import { Contact as ContactComponent } from './contact';
import { AdsContainer } from '../../containers/ads/ads-container';
import gullviksIcon from '/client/assets/images/logo.png';

export const Contacts = (props: ContactsProps) => {
    const [selectedContactArea, setSelectedContactArea] = createSignal<string>('');

    const sortedAreas = createMemo(() => {
        const order = ['sodra-distrikten', 'norra-distrikten', 'huvudkontor', 'lager'];

        return [...props.contactAreas].sort((a, b) => {
            const aIndex = order.indexOf(a.slug);
            const bIndex = order.indexOf(b.slug);

            if (aIndex === -1 && bIndex === -1) {
                return 0;
            }
            if (aIndex === -1) {
                return 1;
            }
            return -1;
        });
    });

    const contactAreaOptions = (): SelectOption[] => {
        const options: SelectOption[] = [
            {
                value: '',
                text: ' -- Visa alla --',
            },
        ];

        sortedAreas().forEach(({ slug, name, children }) => {
            options.push({ value: slug, text: `<strong>${name}</strong>`, disabled: true });

            children?.forEach((child: ContactArea) => {
                options.push({ value: child.slug, text: child.name });
            });
        });

        return options;
    };

    const contactsGroupedByArea = createMemo(() => {
        const groupedContacts: Record<string, { area: ContactArea; contacts: Contact[] }> = {};
        if (!props.contacts.length) {
            return {};
        }

        props.contacts.forEach((contact) => {
            contact.contactAreas.forEach((area) => {
                const slug = area.slug;

                if (!groupedContacts[slug]) {
                    groupedContacts[slug] = {
                        area: area,
                        contacts: [],
                    };
                }

                groupedContacts[slug].contacts.push(contact);
            });
        });

        Object.keys(groupedContacts).forEach((slug) => {
            if (groupedContacts[slug].contacts.length === 0) {
                delete groupedContacts[slug];
            }

            groupedContacts[slug].contacts.sort((a, b) => {
                const areaA = a?.contactAreas?.find((area) => area.slug === slug);
                const areaB = b?.contactAreas?.find((area) => area.slug === slug);

                if (areaA && areaB) {
                    return areaA?.priority - areaB?.priority;
                }

                return 0;
            });
        });

        return groupedContacts;
    });

    const filteredContactAreas = createMemo(() => {
        const contacts = Object.values(contactsGroupedByArea())
            .filter((contactArea) => (selectedContactArea() ? selectedContactArea() === contactArea.area.slug : true))
            .filter((contactArea) => contactArea.contacts.length > 0);

        return contacts.sort((a, b) => {
            const aName = a.area.name.toLocaleLowerCase('sv-SE');
            const bName = b.area.name.toLocaleLowerCase('sv-SE');

            // Assign priority: 0 = normal, 1 = kontor, 2 = lager
            const getPriority = (name: string) => {
                if (name.includes('lager')) {
                    return 2;
                }

                return name.includes('kontor') ? 1 : 0;
            };

            const aPriority = getPriority(aName);
            const bPriority = getPriority(bName);

            return aPriority - bPriority || a.area.name.localeCompare(b.area.name, 'sv-SE');
        });
    });

    const extractAddressLinkInfo = (htmlString: string) => {
        if (!htmlString) {
            return null;
        }

        const match = htmlString.match(/<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/i);
        if (!match) return null;

        return {
            href: match[1],
            text: match[2].trim(),
        };
    };

    const getContactAreaAddressRecursively: (slug: string, areas?: ContactArea[]) => string = (
        slug: string,
        areas = props.contactAreas
    ) => {
        for (const area of areas) {
            if (area.slug === slug) {
                return area.address;
            }
            if (area.children && area.children.length > 0) {
                const result = getContactAreaAddressRecursively(slug, area.children);
                if (result) {
                    return result;
                }
            }
        }

        return '';
    };

    const getContactAreaAddress = (slug: string) => {
        const addressHtml = getContactAreaAddressRecursively(slug);
        const addressInfo = extractAddressLinkInfo(addressHtml);
        return addressInfo;
    };

    const genericContact: Contact = {
        name: 'Kontakta oss',
        email: 'info@gullviks.se',
        tel: '040-680 68 20',
        jobTitle: 'Gullviks',
        imageSrc: '',
        contactAreas: [],
    };

    return (
        <StyledContacts>
            <StyledContactHeadline>Kontakta oss</StyledContactHeadline>
            <StyledSelectWrapper>
                <Select mode="basic" onChange={setSelectedContactArea} options={contactAreaOptions()} value={selectedContactArea()} />
            </StyledSelectWrapper>
            <For
                each={filteredContactAreas()}
                fallback={<StyledNoContactsContainer>Inga kontakter hittades i området</StyledNoContactsContainer>}
            >
                {(contactArea) => {
                    const addressInfo = getContactAreaAddress(contactArea.area.slug);
                    return (
                        <div style={{ width: '100%' }}>
                            <StyledAreaHeader>
                                <h2 style={{ 'margin-bottom': 0 }}>{contactArea.area.name}</h2>
                                <StyledAddressContainer>
                                    <svg
                                        aria-hidden
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        fill={colors.red}
                                        viewBox="0 0 256 256"
                                    >
                                        <path d="M128,16a88.1,88.1,0,0,0-88,88c0,75.3,80,132.17,83.41,134.55a8,8,0,0,0,9.18,0C136,236.17,216,179.3,216,104A88.1,88.1,0,0,0,128,16Zm0,56a32,32,0,1,1-32,32A32,32,0,0,1,128,72Z"></path>
                                    </svg>
                                    <a href={addressInfo?.href || '#'} target="_blank" rel="noopener noreferrer">
                                        {addressInfo?.text}
                                    </a>
                                </StyledAddressContainer>
                            </StyledAreaHeader>

                            <StyledContactsList>
                                <For each={contactArea.contacts}>{(contact) => <ContactComponent contact={contact} />}</For>
                            </StyledContactsList>
                        </div>
                    );
                }}
            </For>
            <StyledAreaHeader />
            <StyledContactsList>
                <StyledGenericContactWrapper>
                    <img src={gullviksIcon} alt="Gullviks logo" width={150} />
                    <ContactComponent contact={genericContact} />
                </StyledGenericContactWrapper>
            </StyledContactsList>
            <AdsContainer placement={Placements.Contacts} />
        </StyledContacts>
    );
};
